import { styled } from '@mui/system';
import InfoPopover from 'Components/InfoPopover';
import OptionChooser from 'Components/OptionChooser';
import { exists } from 'neuro-utils';
import * as React from 'react';

import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import InputHandler from 'Components/InputHandler';
import colors from '../../../../../config/theme/colors';
import { IFormContext, withFormContext } from 'Containers/FormContextHandler';
import { iadl } from 'neuro-schemas';
import { LocalizerCtx } from 'Containers/Localizer';

const {
  calculateScore,
  utils: { iadlNumberFormatter },
} = iadl;

const ReferenceText = styled('div')({ color: colors.tertiaryText });

const iadlFields: Array<{ name: keyof IIADL; options: Array<NonNullable<IIADL[SafeExclude<keyof IIADL, 'date'>]>> }> = [
  { name: 'phoneUsage', options: ['1a', '1b', '1c', '0'] },
  { name: 'shopping', options: ['1', '0a', '0b', '0c'] },
  { name: 'cooking', options: ['1', '0a', '0b', '0c'] },
  { name: 'housekeeping', options: ['1a', '1b', '1c', '1d', '0'] },
  { name: 'laundering', options: ['1a', '1b', '0'] },
  { name: 'transportation', options: ['1a', '1b', '1c', '0a', '0b'] },
  { name: 'responsibilityOfOwnMedication', options: ['1', '0a', '0b'] },
  { name: 'abilityToTakeCareOfFinancialMatters', options: ['1a', '1b', '0'] },
];

const IADLForm = ({ formData, view, fm }: IFormContext<IDocumentWithTypeV2<'iadl'>>): React.JSX.Element => {
  const { locale } = React.useContext(LocalizerCtx);
  const viewing = !!view?.viewing;

  const notFilledFields = iadlFields.filter(field => !formData.document[field.name]);

  const localizations = iadl.getLocalizations(locale ?? 'fi');
  const localize = (id: keyof typeof localizations): string => {
    const localized = localizations[id];
    return localized ?? String(id);
  };

  return (
    <React.Fragment>
      <FormRow title="general.date">
        <InputHandler
          type="PartialDate"
          editing={!viewing}
          name="date"
          formData={formData}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>
      <FormSection>
        {iadlFields.map((field, index) => (
          <FormRow
            key={field.name + index}
            title={localize(`${field.name}.title`)}
            formatTitle={false}
            bottomMargin={true}
          >
            <OptionChooser
              formData={formData}
              name={field.name}
              opts={field.options}
              optionFormatter={(name: string | number) => <span>{localize(`${field.name}.opts.${name}`)}</span>}
              numberFormatter={iadlNumberFormatter}
              viewing={viewing}
            />
          </FormRow>
        ))}
      </FormSection>
      <FormSection>
        <FormRow title="diagnosticSurvey.iadl.score">
          {exists(calculateScore(formData.document)) ? (
            <div style={{ fontWeight: 600 }}>{calculateScore(formData.document)}</div>
          ) : (
            <InfoPopover
              text={
                <div style={{ whiteSpace: 'pre-line' }}>
                  <div style={{ fontWeight: 600 }}>{fm('diagnosticSurvey.iadl.notFilledPartial')}</div>
                  {notFilledFields.map(field => `${localize(`${field.name}.title`)}\n`).join('')}
                </div>
              }
              color="primary"
            />
          )}
        </FormRow>
        <ReferenceText>{fm('diagnosticSurvey.iadl.referenceText')}</ReferenceText>
      </FormSection>
    </React.Fragment>
  );
};

export default withFormContext(IADLForm);
